import { EntityId, createSelector } from "@reduxjs/toolkit";

import { DateTime } from "luxon";

import { RootState } from "app/store";
import { playbackSessionSelectors } from "./selectors.session";

import { Recording } from "types/Recording";

import { getRecordingWithPlaybackState } from "./helpers";
import { appFeaturesSelectors } from "features/appFeatures/store/selectors";
import { AppFeature } from "features/appFeatures/types/AppFeature";

const selectShowStatistics = createSelector(
    [
        (state: RootState) =>
            appFeaturesSelectors.selectUserHasFeatures(state, [
                AppFeature.AdminDebugTools,
            ]),
        (state: RootState) => state.playback.debugShowStatistics,
    ],
    (hasDebugModeModeAccess: boolean, debugShowStatistics: boolean) =>
        hasDebugModeModeAccess && debugShowStatistics,
);

const selectShowBufferingRecordings = createSelector(
    [
        (state: RootState) =>
            appFeaturesSelectors.selectUserHasFeatures(state, [
                AppFeature.AdminDebugTools,
            ]),
        (state: RootState) => state.playback.debugShowBufferingRecordings,
    ],
    (hasAdminDebugTools: boolean, debugShowBufferingRecordings: boolean) =>
        hasAdminDebugTools && debugShowBufferingRecordings,
);

const selectShowRecordingsOutsidePlaybackAndBufferingWindow = createSelector(
    [
        (state: RootState) =>
            appFeaturesSelectors.selectUserHasFeatures(state, [
                AppFeature.AdminDebugTools,
            ]),
        (state: RootState) =>
            state.playback.debugShowRecordingsOutsidePlaybackAndBufferingWindow,
    ],
    (
        hasAdminDebugTools: boolean,
        debugShowRecordingsOutsidePlaybackAndBufferingWindow: boolean,
    ) =>
        hasAdminDebugTools &&
        debugShowRecordingsOutsidePlaybackAndBufferingWindow,
);

const selectRecordingsWithPlaybackStateByDeviceIdFactory = () =>
    createSelector(
        [
            (state: RootState, deviceId: EntityId) => deviceId,
            playbackSessionSelectors.selectAllRecordings,
            playbackSessionSelectors.selectCurrentPlaybackTimeStamp,
            playbackSessionSelectors.selectBufferDateTimeRange,
        ],
        (
            deviceId: EntityId,
            recordings: Recording[],
            currentPlaybackTimeStamp: number,
            bufferWindow: { startDateTime: string; endDateTime: string },
        ) => {
            const deviceRecordings = recordings.filter(
                (x) => x.deviceId === deviceId,
            );

            const bufferStartDateTimeTicks = DateTime.fromISO(
                bufferWindow.startDateTime,
            ).valueOf();
            const bufferEndDateTimeTicks = DateTime.fromISO(
                bufferWindow.endDateTime,
            ).valueOf();

            return deviceRecordings.map((recording) =>
                getRecordingWithPlaybackState(
                    bufferStartDateTimeTicks,
                    bufferEndDateTimeTicks,
                    currentPlaybackTimeStamp,
                    recording,
                ),
            );
        },
    );

export const playbackDebugSelectors = {
    selectShowStatistics,
    selectShowBufferingRecordings,
    selectShowRecordingsOutsidePlaybackAndBufferingWindow,
    selectRecordingsWithPlaybackStateByDeviceIdFactory,
};
